/* istanbul ignore file -- api file */
import { useServiceUrls } from '@ori/configuration-manager';
import { useGraphqlQuery } from '@ori/fetching';
import { useHeaders } from '@ori/presentation-http';

import { mainLogger } from '../logger';
import type { TopAreaBannersQuery, TopAreaBannersQueryVariables } from '../models/graphql-types/net';
import { PermanentTopBannerType } from '../models/graphql-types/net';
import { topAreaBanners } from '../queries-net';
import { mapTopAreaBanners } from '../utils/mapTopAreaBanners';

interface ShouldSkipTopAreaBannersFetchParams {
  enableTopAreaBanner?: boolean;
  loadingSegmentationData: boolean;
  orderOnBehalfVisible: boolean;
  bannerType: PermanentTopBannerType | null;
}

const logger = mainLogger.createChild('useGetTopAreaBanners');

export const shouldSkipTopAreaBannersFetch = ({
  enableTopAreaBanner,
  loadingSegmentationData,
  orderOnBehalfVisible,
  bannerType,
}: ShouldSkipTopAreaBannersFetchParams): boolean =>
  loadingSegmentationData || orderOnBehalfVisible || !enableTopAreaBanner || bannerType === null;

interface UseGetTopAreaBannersParams {
  bannerType: PermanentTopBannerType | null;
  disableFetching?: boolean;
}
export const useGetTopAreaBanners = ({ bannerType, disableFetching = false }: UseGetTopAreaBannersParams) => {
  const { graphQlNetUrl } = useServiceUrls(['graphQlNet']);
  const { headers } = useHeaders();

  const {
    data,
    isValidating: loading,
    error: apiError,
  } = useGraphqlQuery<TopAreaBannersQuery, TopAreaBannersQueryVariables>({
    url: graphQlNetUrl,
    query: topAreaBanners,
    headers,
    variables: {
      bannerType: bannerType ?? PermanentTopBannerType.PublicPage,
    },
    skip: disableFetching,
    onError: (error) => {
      logger.error(error);
    },
    swrConfig: {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  return {
    bannersData: data && bannerType !== null ? mapTopAreaBanners({ data }) : undefined,
    error: apiError,
    loading,
  };
};
